import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Can } from '@/components/auth/Can';
import { UNE_PERM } from '@/constants';
import type { SICRunResult } from '@/types/sic-report.types';
import { isSICRunSkipped } from '@/types/sic-report.types';

interface Props {
    partnerId: string;
    isRunning: boolean;
    runResult: SICRunResult | undefined;
    runError: Error | null;
    onRunManual: (input: { runDate?: string; force?: boolean }) => void;
}

export function SICActionsCard({ partnerId, isRunning, runResult, runError, onRunManual }: Props) {
    const [showConfirm, setShowConfirm] = useState(false);
    const [runDate, setRunDate] = useState<string>(new Date().toISOString().split('T')[0] ?? '');
    const force = true;

    const handleSubmit = () => {
        onRunManual({ runDate, force });
        setShowConfirm(false);
    };

    return (
        <>
            <Card title="Acciones" description="Disparar corrida manual (solo debugging / recovery)">
                <div className="flex items-center gap-3">
                    <Can permission={UNE_PERM.SIC_RUN}>
                        <Button onClick={() => setShowConfirm(true)} disabled={isRunning}>
                            {isRunning ? 'Procesando…' : 'Disparar corrida manual'}
                        </Button>
                    </Can>
                    <span className="text-sm text-slate-400">
                        Estado SIC para {partnerId}: <span className="text-emerald-400">ACTIVO</span>
                    </span>
                </div>

                {runResult && !isSICRunSkipped(runResult) && (
                    <div className="mt-3 rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
                        Corrida completada: {runResult.eligible} elegibles, {runResult.confirmed} confirmados,
                        {runResult.rejected} rechazados, {runResult.error} errores.
                    </div>
                )}

                {runResult && isSICRunSkipped(runResult) && (
                    <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
                        {runResult.reason}
                    </div>
                )}

                {runError && (
                    <div className="mt-3 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                        Error: {runError.message}
                    </div>
                )}
            </Card>

            {showConfirm && (
                <Modal isOpen onClose={() => setShowConfirm(false)} title="Disparar corrida manual">
                    <div className="space-y-3">
                        <p className="text-sm text-slate-300">
                            Vas a ejecutar una corrida SIC manual para <strong>{partnerId}</strong>.
                            Esto envía a Círculo de Crédito todas las cuentas elegibles en el delta.
                        </p>
                        <label className="block">
                            <span className="text-xs text-slate-400">Fecha de corrida</span>
                            <input
                                type="date"
                                value={runDate}
                                onChange={(e) => setRunDate(e.target.value)}
                                className="mt-1 block w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200"
                            />
                        </label>
                        <p className="text-xs text-slate-500">
                            Si ya existe una corrida ese día, se sobrescribe automáticamente (debugging / recovery).
                        </p>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancelar</Button>
                            <Button onClick={handleSubmit}>Confirmar</Button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}
